import React, { useState, useEffect } from 'react';
import { OrganizerRow } from './OrganizerRow';
import { EditOrganizerModal } from './EditOrganizerModal';
import { useOrganizersStore } from '../../stores/organizersStore';
import { Organizer } from '../../types/organizer';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { ErrorMessage } from '../../components/common/ErrorMessage';
import { ConfirmDialog } from '../../components/common/ConfirmDialog';
import { useConfirm } from '../../hooks/useConfirm';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { ReplaceAuthModal } from './ReplaceAuthModal';

type Props = {
  onSyncNameClick?: (organizerId: string) => void;
};

export const OrganizersList = ({ onSyncNameClick }: Props) => {
  const [editingOrganizer, setEditingOrganizer] = useState<Organizer | null>(null);
  const [replacingAuth, setReplacingAuth] = useState<Organizer | null>(null);
  const { isOpen, config, confirm, close } = useConfirm();
  const { organizers, loading, error, deleteOrganizer } = useOrganizersStore();
  const { translations } = useLanguage();

  const handleDelete = async (organizer: Organizer) => {
    try {
      await deleteOrganizer(organizer.id);
      toast.success(translations?.organizersList?.deleteSuccess || 'Organizer deleted successfully');
    } catch (error: any) {
      console.error('Error deleting organizer:', error);
      toast.error(translations?.organizersList?.deleteError || 'Failed to delete organizer');
    }
  };

  const confirmDelete = (organizer: Organizer) => {
    confirm({
      title: translations?.organizersList?.deleteTitle || 'Delete Organizer',
      message: `${translations?.organizersList?.deleteMessage || 'Are you sure you want to delete the organizer'} "${organizer.name}"?`,
      onConfirm: () => handleDelete(organizer),
      type: 'danger',
      confirmText: translations?.organizersList?.confirmDelete || 'Delete',
      cancelText: translations?.organizersList?.cancelDelete || 'Cancel',
    });
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  return (
    <>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {/* Desktop Table */}
        <table className="hidden md:table min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                {translations?.organizersList?.headers?.name || 'Name'}
              </th>
              <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                {translations?.organizersList?.headers?.email || 'Email'}
              </th>
              <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                {translations?.organizersList?.headers?.country || 'Country'}
              </th>
              <th className="px-6 py-3 text-right text-sm font-medium text-gray-500">
                {translations?.organizersList?.headers?.actions || 'Actions'}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Object.values(organizers).map((organizer) => (
              <OrganizerRow
                key={organizer.id}
                organizer={organizer}
                onEdit={setEditingOrganizer}
                onDelete={confirmDelete}
                onSyncName={onSyncNameClick}
                onReplaceAuth={setReplacingAuth}
              />
            ))}
          </tbody>
        </table>

        {/* Mobile List */}
        <div className="md:hidden">
          {Object.values(organizers).map((organizer) => (
            <OrganizerRow
              key={organizer.id}
              organizer={organizer}
              onEdit={setEditingOrganizer}
              onDelete={confirmDelete}
              onSyncName={onSyncNameClick}
              onReplaceAuth={setReplacingAuth}
            />
          ))}
        </div>
      </div>

      {editingOrganizer && (
        <EditOrganizerModal
          isOpen={true}
          onClose={() => setEditingOrganizer(null)}
          organizer={editingOrganizer}
        />
      )}

      {replacingAuth && (
        <ReplaceAuthModal
          isOpen={true}
          onClose={() => setReplacingAuth(null)}
          organizer={replacingAuth}
        />
      )}

      {isOpen && config && (
        <ConfirmDialog
          isOpen={isOpen}
          onClose={close}
          onConfirm={config.onConfirm}
          title={config.title}
          message={config.message}
          type={config.type}
          confirmText={translations?.organizersList?.confirmDelete || 'Delete'}
          cancelText={translations?.organizersList?.cancelDelete || 'Cancel'}
        />
      )}
    </>
  );
};